/**
 * DialogFooter Component
 *
 * Reusable dialog footer component with action buttons, theme integration,
 * and support for various configurations. Uses ONLY theme tokens - no hardcoded styles.
 */
import React, { memo } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

/**
 * Configuration for footer action buttons
 */
export interface FooterActionConfig {
  /**
   * Button label text
   */
  label: string;

  /**
   * Click handler function
   */
  onClick: () => void | Promise<void>;

  /**
   * Button variant styling
   * @default "contained"
   */
  variant?: 'contained' | 'outlined' | 'text';

  /**
   * Button color theme
   * @default "primary"
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Whether button is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether button is in loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Icon to display before label
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to display after label
   */
  endIcon?: React.ReactNode;

  /**
   * Custom styles using theme-aware sx prop
   */
  sx?: SxProps<Theme>;

  /**
   * Test identifier for automated testing
   */
  'data-testid'?: string;
}

/**
 * DialogFooter component props interface
 */
export interface DialogFooterProps {
  /**
   * Primary action button configuration (typically "Save", "Submit", etc.)
   */
  primaryAction?: FooterActionConfig;

  /**
   * Secondary action button configuration (typically "Cancel", "Close", etc.)
   */
  secondaryAction?: FooterActionConfig;

  /**
   * Additional action buttons configuration
   */
  additionalActions?: FooterActionConfig[];

  /**
   * Button alignment within the footer
   * @default "space-between"
   */
  alignment?: 'left' | 'center' | 'right' | 'space-between';

  /**
   * Visual variant affecting spacing and styling
   * @default "default"
   */
  variant?: 'default' | 'compact' | 'minimal';

  /**
   * Whether to show a top divider
   * @default true
   */
  divider?: boolean;

  /**
   * Helper text to display in the footer (typically on the left)
   */
  helperText?: string;

  /**
   * Custom styles using theme-aware sx prop
   */
  sx?: SxProps<Theme>;

  /**
   * Accessibility label for the footer
   */
  'aria-label'?: string;

  /**
   * Test identifier for automated testing
   */
  'data-testid'?: string;
}

/**
 * DialogFooter Component
 *
 * A flexible and theme-aware dialog footer component that supports:
 * - Primary, secondary, and additional action buttons
 * - Loading and disabled states for all buttons
 * - Flexible alignment options
 * - Helper text display
 * - Multiple visual variants
 * - Full theme integration without hardcoded styles
 */
const DialogFooterComponent: React.FC<DialogFooterProps> = ({
  primaryAction,
  secondaryAction,
  additionalActions = [],
  alignment = 'space-between',
  variant = 'default',
  divider = true,
  helperText,
  sx,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'dialog-footer',
}) => {
  const theme = useTheme();

  // Memoized theme-based style calculations for performance
  const themeStyles = React.useMemo(() => {
    const getPadding = () => {
      switch (variant) {
        case 'compact':
          return theme.spacing(1);
        case 'minimal':
          return theme.spacing(0.5);
        default:
          return theme.spacing(2);
      }
    };

    const getGap = () => {
      switch (variant) {
        case 'compact':
        case 'minimal':
          return theme.spacing(1);
        default:
          return theme.spacing(2);
      }
    };

    return {
      padding: getPadding(),
      gap: getGap(),
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
    };
  }, [theme, variant]);

  // Memoized button rendering function to avoid unnecessary re-renders
  const renderActionButton = React.useCallback(
    (action: FooterActionConfig, isDefault = false) => {
      const {
        label,
        onClick,
        variant: buttonVariant = isDefault ? 'contained' : 'outlined',
        color = 'primary',
        disabled = false,
        loading = false,
        startIcon,
        endIcon,
        sx: buttonSx,
        'data-testid': buttonTestId,
      } = action;

      // Show loading icon if loading state is active
      const actualStartIcon = loading ? (
        <CircularProgress
          size={16}
          sx={{ color: 'inherit' }}
          data-testid={`${buttonTestId || 'action-button'}-loading`}
        />
      ) : startIcon;

      return (
        <Button
          key={label}
          variant={buttonVariant}
          color={color}
          disabled={disabled || loading}
          onClick={onClick}
          startIcon={actualStartIcon}
          endIcon={!loading ? endIcon : undefined}
          data-testid={buttonTestId || `dialog-footer-${label.toLowerCase().replace(/\s+/g, '-')}`}
          // Array-form sx: composes object AND function (theme-callback)
          // entries — a plain `{ ...buttonSx }` spread silently drops
          // function-form sx (design review 2026-08-04).
          sx={[
            { minWidth: theme.spacing(12) }, // Consistent minimum button width
            ...(Array.isArray(buttonSx) ? buttonSx : [buttonSx]),
          ]}
        >
          {label}
        </Button>
      );
    },
    [theme]
  );

  // Determine layout configuration based on alignment
  const getLayoutStyles = () => {
    switch (alignment) {
      case 'left':
        return { justifyContent: 'flex-start' };
      case 'center':
        return { justifyContent: 'center' };
      case 'right':
        return { justifyContent: 'flex-end' };
      case 'space-between':
      default:
        return { justifyContent: 'space-between' };
    }
  };

  // Collect all actions for rendering
  const allActions = [
    ...(secondaryAction ? [secondaryAction] : []),
    ...additionalActions,
    ...(primaryAction ? [primaryAction] : []),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: themeStyles.padding,
        backgroundColor: themeStyles.backgroundColor,
        borderTop: divider ? `1px solid ${themeStyles.borderColor}` : 'none',
        minHeight: theme.spacing(7), // Consistent minimum height
        ...getLayoutStyles(),
        ...sx,
      }}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      role="toolbar"
    >
      {/* Left section: Helper text and secondary actions (for space-between) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: themeStyles.gap,
          flex: alignment === 'space-between' ? 1 : 'none',
        }}
        data-testid={`${dataTestId}-left-section`}
      >
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.2,
            }}
            data-testid={`${dataTestId}-helper-text`}
          >
            {helperText}
          </Typography>
        )}

        {/* For space-between: secondary action goes on the left */}
        {alignment === 'space-between' && secondaryAction && renderActionButton(secondaryAction)}
      </Box>

      {/* Right section: Action buttons */}
      {allActions.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: themeStyles.gap,
            justifyContent: alignment === 'space-between' ? 'flex-end' : 'inherit',
          }}
          data-testid={`${dataTestId}-actions`}
        >
          {/* For non-space-between: render secondary first */}
          {alignment !== 'space-between' && secondaryAction && renderActionButton(secondaryAction)}

          {/* Additional actions */}
          {additionalActions.map((action, index) =>
            renderActionButton({
              ...action,
              'data-testid': action['data-testid'] || `dialog-footer-additional-${index}`,
            })
          )}

          {/* Primary action (always rightmost) */}
          {primaryAction && renderActionButton(primaryAction, true)}
        </Box>
      )}
    </Box>
  );
};

/**
 * Memoized DialogFooter component for performance optimization
 * Prevents unnecessary re-renders when props haven't changed
 *
 * Custom comparison function for shallow prop comparison
 */
export const DialogFooter = memo(DialogFooterComponent, (prevProps, nextProps) => {
  // Shallow comparison of primitive props
  const primitiveKeys: (keyof DialogFooterProps)[] = [
    'alignment', 'variant', 'divider', 'helperText', 'aria-label', 'data-testid'
  ];

  // Check primitive props
  for (const key of primitiveKeys) {
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }

  // Deep comparison for action objects (since these contain functions)
  const compareAction = (
    prev: FooterActionConfig | undefined,
    next: FooterActionConfig | undefined
  ): boolean => {
    if (prev === next) return true;
    if (!prev || !next) return false;

    // Compare all properties of action config
    const keys: (keyof FooterActionConfig)[] = [
      'label', 'variant', 'color', 'disabled', 'loading', 'data-testid'
    ];

    for (const key of keys) {
      if (prev[key] !== next[key]) {
        return false;
      }
    }

    // Compare function references
    if (prev.onClick !== next.onClick) {
      return false;
    }

    // Compare icon references
    if (prev.startIcon !== next.startIcon || prev.endIcon !== next.endIcon) {
      return false;
    }

    // Compare sx objects (reference comparison)
    if (prev.sx !== next.sx) {
      return false;
    }

    return true;
  };

  // Compare action configurations
  if (!compareAction(prevProps.primaryAction, nextProps.primaryAction)) {
    return false;
  }

  if (!compareAction(prevProps.secondaryAction, nextProps.secondaryAction)) {
    return false;
  }

  // Compare additional actions array
  if (prevProps.additionalActions?.length !== nextProps.additionalActions?.length) {
    return false;
  }

  if (prevProps.additionalActions && nextProps.additionalActions) {
    for (let i = 0; i < prevProps.additionalActions.length; i++) {
      if (!compareAction(prevProps.additionalActions[i], nextProps.additionalActions[i])) {
        return false;
      }
    }
  }

  // Compare sx objects (reference comparison - parents should memoize)
  if (prevProps.sx !== nextProps.sx) {
    return false;
  }

  return true;
});

// Set display name for debugging
DialogFooter.displayName = 'DialogFooter';

// Default export
export default DialogFooter;